import React, { useCallback, useEffect, useRef, useState } from "react"
import { GridContainer } from "../components/layout/GridContainer";
import { Input } from "../components/tag/Input";
import { Button } from "../components/tag/Button";
import { usePopup } from "../components/utils/PopupProvider";
import IMG from "../assets/img/Img";
import { FeederCard } from "../components/feeder/FeederCard";
import { FeederSchedulePopup } from "../components/feeder/FeederSchedulePopup";
import { setStateSAMO, stateInvalidCheck } from "../components/utils/State";
import { queryBuilder } from "../components/service/QueryBuilder";
import { showToast } from "../components/utils/Toastify";
import { getIsoDateTime, getScheduleMetrics, getTimeOfDayInMs, setDefaultDate, utcToLocal } from "../components/utils/Date";
import { useGraphqlService } from "../components/service/GraphqlService";
import { useConvertOrderService } from "../components/service/ConverOrderService";
import { DefDiv, PageTitle, SectionBox, SectionTitle, SubTitle, InfoText, FormField } from "../components/tag/CustomTag";

const DEFAULT_FEED_SIZE = 6;

export const FeederPage = () => {
    const { executeMutation, executeQuery, objToQueryData } = useGraphqlService();
    const { cfc } = useConvertOrderService();
    const { showPopup, hidePopup, hideLastPopup, updatePopup } = usePopup();

    const popupIdRef = useRef(null);
    const feedScheduleRef = useRef([]);

    const [outlet, setOutlet] = useState(() => {
        return Array.from({ length: 12 }, (_, i) => ({ outlet: (i+1), quantity: 0, time: 0, active: 1 }));
    });
    const [totalSize, setTotalSize] = useState(0);
    const [scheduleNo, setScheduleNo] = useState(null);
    const [scheduleTime, setScheduleTime] = useState("");
    const [size, setSize] = useState(DEFAULT_FEED_SIZE);
    const [batchQuantity, setBatchQuantity] = useState(0);
    const [batchTime, setBatchTime] = useState(0);
    const [feedSchedule, setFeedSchedule] = useState([]);
    const [machineInfo, setMachineInfo] = useState([]);
    const [upComingSchedules, setUpComingSchedules] = useState([]);
    const [inProgressSchedules, setInProgressSchedules] = useState([]);
    const [completedSchedules, setCompletedSchedules] = useState([]);
    const [currentFeedStatus, setCurrentFeedStatus] = useState('대기중');
    const [activeOutlets, setActiveOutlets] = useState([]);
    const [lastFeedTime, setLastFeedTime] = useState(null);

    const fetchFeedSchedule = useCallback(async () => {
        const { data } = await executeQuery(
            queryBuilder(true, false, { info: 'feedSchedule' }) +
            queryBuilder(false, true, { info: 'machine' })
        );
        if (data) {            
            setFeedSchedule(data.getFeedSchedule);
            setMachineInfo(data.getMachineInfo);
            feedScheduleRef.current = data.getFeedSchedule;
        }
    }, [executeQuery]);

    const classifySchedules = useCallback((schedules) => {
        if (!schedules?.length) {
            setUpComingSchedules([]);
            setInProgressSchedules([]);
            setCompletedSchedules([]);
            return { upcoming: [], inProgress: [], completed: [] };
        }

        const nowMs = getTimeOfDayInMs(new Date());
        const upcoming = [], inProgress = [], completed = [];

        [...schedules]
            .filter(i => i.DEL_YN === 'N')
            .sort((a, b) => getTimeOfDayInMs(a.SCHEDULE_TIME) - getTimeOfDayInMs(b.SCHEDULE_TIME))
            .forEach(schedule => {
                const { scheduleMs, endTimeMs } = getScheduleMetrics(schedule);
                if (nowMs < scheduleMs) upcoming.push(schedule);
                else if (nowMs < endTimeMs) inProgress.push(schedule);
                else completed.push(schedule);
            });

        setUpComingSchedules(upcoming);
        setInProgressSchedules(inProgress);
        setCompletedSchedules(completed);

        return { upcoming, inProgress, completed };
    }, []);

    const checkSchedule = useCallback(({ upcoming, inProgress, completed }) => {
        const nowMs = getTimeOfDayInMs(new Date());

        if (inProgress.length > 0) {
            const currentSchedule = inProgress[0];
            const { scheduleMs, activeFeeds } = getScheduleMetrics(currentSchedule);
            const elapsedMs = nowMs - scheduleMs;

            let cumulativeMs = 0;
            let currentOutlet = null;

            for (const feed of [...activeFeeds].sort((a, b) => a.outlet - b.outlet)) {
                cumulativeMs += feed.time * 100;
                if (elapsedMs < cumulativeMs) { currentOutlet = feed.outlet; break; }
            }

            const scheduleTimeStr = new Date(currentSchedule.SCHEDULE_TIME)
                .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            setCurrentFeedStatus(`사료 공급 중 (스케줄: ${scheduleTimeStr})`);
            setActiveOutlets(currentOutlet !== null ? [currentOutlet] : []);

        } else if (upcoming.length > 0) {
            const { scheduleMs } = getScheduleMetrics(upcoming[0]);
            const timeUntilMs = scheduleMs - nowMs;

            if (timeUntilMs > 0 && timeUntilMs <= 3600000) {
                const scheduleTimeStr = new Date(upcoming[0].SCHEDULE_TIME)
                    .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                setCurrentFeedStatus(`사료 공급 준비 중 (예약까지 ${Math.ceil(timeUntilMs / 60000)}분, 예약시간: ${scheduleTimeStr})`);
            } else {
                setCurrentFeedStatus('대기중');
            }
            setActiveOutlets([]);

        } else {
            setCurrentFeedStatus('사료 공급 완료');
            if (completed.length > 0) {
                setLastFeedTime(new Date(completed[completed.length - 1].SCHEDULE_TIME).toLocaleTimeString());
            }
            setActiveOutlets([]);
        }
    }, []);

    const runScheduleCheck = useCallback((schedules) => {
        const result = classifySchedules(schedules);
        if (result) checkSchedule(result);
    }, [classifySchedules, checkSchedule]);

    useEffect(() => {
        fetchFeedSchedule();
        const interval = setInterval(() => runScheduleCheck(feedScheduleRef.current), 3000);
        return () => clearInterval(interval);
    }, [fetchFeedSchedule, runScheduleCheck]);

    useEffect(() => {
        setTotalSize(outlet.reduce((acc, item) => acc + (parseInt(item.quantity) || 0), 0));
    }, [outlet]);

    useEffect(() => {
        if (!feedSchedule?.length) return;
        runScheduleCheck(feedSchedule);
    }, [feedSchedule, runScheduleCheck]);

    useEffect(() => {
        if (!popupIdRef.current) return;
        updatePopup(popupIdRef.current, buildPopupContent(popupIdRef.current));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feedSchedule]);

    const batchFeedSchedule = () => {
        setOutlet(prev => prev.map(item => ({
            ...item,
            quantity: batchQuantity || 0,
            time: batchTime || 0,
        })));
    };

    const getRemainingTime = (schedule) => {
        const remainingMs = getTimeOfDayInMs(schedule.SCHEDULE_TIME) - getTimeOfDayInMs(new Date());
        if (remainingMs > 0 && remainingMs <= 3600000) {
            return ` (예약까지 ${Math.ceil(remainingMs / 60000)}분 남음)`;
        }
        return '';
    };

    const buildPopupContent = (id) => (
        <FeederSchedulePopup
            feedSchedule={feedSchedule}
            viewDetail={viewDetail}
            toggleDelYn={toggleDelYn}
            updateFeedSchedule={updateFeedSchedule}
            deleteFeedSchedule={deleteFeedSchedule}
            transmitData={transmitData}
            onClose={() => { hidePopup(id); popupIdRef.current = null; }}
        />
    );

    const showFeederSchedulePopup = () => {
        let id;
        id = showPopup(buildPopupContent(id), { className: "max-w-[500px]", closeOnOverlayClick: true });
        popupIdRef.current = id;
        updatePopup(id, buildPopupContent(id));
    };

    const toggleDelYn = async (scheduleNo, enabled) => {
        const gqlMutation = `mutation { changeDelynFeedSchedule(data: {NO: ${scheduleNo}, DEL_YN: "${enabled ? "N" : "Y"}"}) }`;
        try {
            const result = await executeMutation(gqlMutation);
            if (result) { showToast.success("사용중지 상태가 변경되었습니다."); await fetchFeedSchedule(); }
            else showToast.error("사용중지 상태 변경에 실패했습니다.");
        } catch {
            showToast.error("사용중지 상태 변경 중 오류가 발생했습니다.");
        }
    };

    const viewDetail = (scheduleNo) => {
        const schedule = feedSchedule.find(s => s.NO === scheduleNo);
        if (!schedule) return showToast.error("잘못된 정보입니다.");

        const reservationData = JSON.parse(schedule.RESERVATION_DATA);
        setSize(reservationData.SIZE || DEFAULT_FEED_SIZE);
        setScheduleTime(setDefaultDate(new Date(schedule.SCHEDULE_TIME)));
        setOutlet(reservationData.feed_set);
        hideLastPopup();
    };

    const updateFeedSchedule = (scheduleNo) => {
        setScheduleNo(scheduleNo);
        viewDetail(scheduleNo);
    };

    const deleteFeedSchedule = async (scheduleNo) => {
        try {
            const result = await executeMutation(`mutation { deleteFeedSchedule(data: {NO: ${scheduleNo}}) }`);
            if (result) { showToast.success("스케줄이 삭제되었습니다."); await fetchFeedSchedule(); }
            else showToast.error("삭제에 실패했습니다.");
        } catch {
            showToast.error("삭제 중 오류가 발생했습니다.");
        }
    };

    const saveFeedSchedule = async () => {        
        if(stateInvalidCheck(scheduleTime)){
            return showToast.error("동작 시간을 입력하세요.");
        }

        const dateScheduleTime = getIsoDateTime(scheduleTime);
        const reservationData = {
            feed_set: outlet.map(item => ({
                outlet: item.outlet,
                quantity: parseInt(item.quantity) || 0,
                time: (parseInt(item.time) || 0),
                active: item.active ? 1 : 0,
            })),
            SIZE: size
        };

        const structuredData = {
            SCHEDULE_TIME: dateScheduleTime,
            RESERVATION_DATA: JSON.stringify(reservationData),
            DEL_YN: 'N',
            ...(scheduleNo && { NO: scheduleNo }),
        };

        try {
            const result = await executeMutation(`mutation { upsertFeedSchedule(data: {${objToQueryData(structuredData)}}) }`);
            if (result) {
                showToast.success("스케줄이 저장되었습니다.");
                await fetchFeedSchedule();
                setScheduleNo(null);
                await transmitJustSavedSchedule(dateScheduleTime, reservationData, size);
            } else {
                showToast.error("스케줄 저장이 실패했습니다.");
            }
        } catch {
            showToast.error("저장 중 오류가 발생했습니다.");
        }
    };

    const buildReservationString = (item) => {
        let rawReservationData = item.RESERVATION_DATA;

        if (rawReservationData.startsWith('"') && rawReservationData.endsWith('"')) {
            rawReservationData = rawReservationData.slice(1, -1);
        }

        let feedSize = DEFAULT_FEED_SIZE;
        let cleanedReservationData = rawReservationData;

        try {
            const parsed = JSON.parse(rawReservationData);
            feedSize = parsed.SIZE || DEFAULT_FEED_SIZE;
            delete parsed.SIZE;
            cleanedReservationData = JSON.stringify(parsed);
        } catch {
            console.warn(`Failed to parse RESERVATION_DATA, using default SIZE: ${DEFAULT_FEED_SIZE}`);
        }

        return `{ "SCHEDULE_TIME": "${utcToLocal(item.SCHEDULE_TIME)}", "RESERVATION_DATA": ${cleanedReservationData}, "DEL_YN": "${item.DEL_YN}", "NO": ${item.NO}, "SIZE":${feedSize} }`;
    };

    const transmitJustSavedSchedule = async (scheduleTime, reservationData, feedSize) => {
        if (!machineInfo) return;

        const cleanedReservationData = { ...reservationData };
        delete cleanedReservationData.SIZE;

        const newScheduleReservation = `{ "SCHEDULE_TIME": "${utcToLocal(scheduleTime)}", "RESERVATION_DATA": ${JSON.stringify(cleanedReservationData)}, "DEL_YN": "N", "NO": 0, "SIZE":${feedSize} }`;
        const existingReservations = feedSchedule.filter(i => i.DEL_YN === 'N').map(buildReservationString);
        console.log(existingReservations.length);
        const jsonData = `{ "reservations": [${[newScheduleReservation, ...existingReservations].join(",")}]}`;

        try {
            await cfc(jsonData, "on", machineInfo);
            showToast.success("전송 완료");
        } catch {
            showToast.error("전송 실패");
        }
    };

    const transmitData = async () => {
        const reservations = feedSchedule.filter(i => i.DEL_YN === 'N').map(buildReservationString);
        console.log(reservations.length);
        const jsonData = `{ "reservations": [${reservations.join(",")}]}`;

        try {
            await cfc(jsonData, "on", machineInfo);
            showToast.success("전송 완료");
        } catch {
            showToast.error("전송 실패");
        }
    };

    return (
        <div className="flex flex-col h-full gap-6">            
            <PageTitle title={"사료공급 시스템"}>
                <Button onClick={showFeederSchedulePopup} className="w-24 h-9 flex items-center justify-center text-sm font-bold text-white bg-[#0092B8] rounded">
                    스케줄 조회
                </Button>
            </PageTitle>

            <div className="flex flex-col lg:flex-row gap-4 flex-1 h-full">
                <DefDiv className="max-w-full lg:max-w-[355px] h-fit">
                    <div className="flex flex-col gap-4 flex-1 overflow-auto">
                        <SectionBox>
                            <div>
                                <SectionTitle>사료 공급 현황</SectionTitle>
                                <SubTitle>({currentFeedStatus})</SubTitle>
                                {currentFeedStatus === '사료 공급 완료' && (
                                    <span className="text-sm text-[#62748E]">{lastFeedTime}</span>
                                )}
                            </div>
                            {feedSchedule?.length > 0 && (
                                <>
                                    {inProgressSchedules.length > 0 && activeOutlets.length > 0 && (
                                        <div>
                                            <SubTitle>공급중인 토출구</SubTitle>
                                            <ul>
                                                {activeOutlets.map((item, i) => (
                                                    <li className="border border-[#00c950] font-bold text-sm animate-[bg-change_2s_infinite] text-white text-center p-2 rounded" key={i}>토출구 번호 : {item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {upComingSchedules.length > 0 && (
                                        <div>
                                            <SubTitle>예정된 스케줄</SubTitle>
                                            {upComingSchedules.map((item, i) => (
                                                <InfoText key={item.NO || i}>
                                                    - 예약시간 : {setDefaultDate(new Date(item.SCHEDULE_TIME))} {getRemainingTime(item)}
                                                </InfoText>
                                            ))}
                                        </div>
                                    )}
                                    {completedSchedules.length > 0 && (
                                        <div>
                                            <SubTitle>완료된 스케줄</SubTitle>
                                            {completedSchedules.map((item, i) => (
                                                <InfoText key={item.NO || i}>
                                                    - 예약시간 : {setDefaultDate(new Date(item.SCHEDULE_TIME))}
                                                </InfoText>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </SectionBox>

                        <SectionBox>
                            <SectionTitle>설정 정보</SectionTitle>
                            <FormField label="동작 시간">
                                <Input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
                                    className="w-full border-2 rounded bg-[#0F172B] border-[#314158] text-[#00D3F3] appearance-none [&::-webkit-calendar-picker-indicator]:invert" />
                            </FormField>
                            <FormField label="사료 호수">
                                <Input type="number" value={size} onChange={e => setSize(e.target.value)}
                                    min="1" max="15" placeholder="6"
                                    className="w-full border-2 rounded bg-[#0F172B] border-[#314158] text-[#00D3F3]" />
                            </FormField>
                            <div className="flex justify-between text-[#62748E]">
                                <span>총 사료량</span>
                                <span className="text-[#F54900]">{totalSize} g</span>
                            </div>
                        </SectionBox>

                        <SectionBox variant="danger">
                            <SectionTitle color="danger" size="lg">일괄 적용</SectionTitle>
                            <FormField label="사료량 (g)">
                                <Input type="text" value={batchQuantity} onChange={e => setBatchQuantity(e.target.value)}
                                    className="w-full border-2 rounded bg-[#0F172B] border-[#314158] text-[#FF8904]" />
                            </FormField>
                            <FormField label="시간 (초)">
                                <Input type="text" value={batchTime} onChange={e => setBatchTime(e.target.value)}
                                    className="w-full border-2 rounded bg-[#0F172B] border-[#314158] text-[#00D3F3]" />
                            </FormField>
                            <Button onClick={batchFeedSchedule} className="w-full h-9 flex items-center justify-center gap-3 text-sm font-bold text-white bg-[#F54900] rounded">
                                <span style={{ backgroundImage: `url(${IMG.CLIP_ICON})` }} className="w-4 h-4" />
                                전체 적용
                            </Button>
                        </SectionBox>

                        <Button onClick={saveFeedSchedule} className="w-full h-9 flex items-center justify-center text-sm font-bold text-white bg-[#0092B8] rounded">
                            스케줄 저장
                        </Button>
                    </div>
                </DefDiv>

                <DefDiv className="h-full">
                    <div className="flex flex-col gap-5 flex-1 overflow-auto">
                        <div className="flex items-center gap-2">
                            <span style={{ backgroundImage: `url(${IMG.COG_ICON})` }} className="w-5 h-5 bg-no-repeat bg-center" />
                            <p className="text-[#00D3F3]">토출구 설정</p>
                        </div>
                        <GridContainer columns={{ base: 1, sm: 2, md: 3, lg: 4 }}>
                            {outlet.map((item, index) => (
                                <FeederCard key={index} item={item} onChange={(key, value) => setStateSAMO(setOutlet, index, key, value)} />
                            ))}
                        </GridContainer>
                    </div>
                </DefDiv>
            </div>
        </div>
    );
};